import type { FairUseTier } from "@/api/admin/fair-use"

// Helpers for the model dialog's "Available to plans" control (#3087, spec R2).
// Kept out of the .tsx so the dialog exports components only (react-refresh).

/** The plans a model may be restricted to, ladder order. Mirrors `VALID_TIERS`. */
export const MODEL_PLAN_IDS: readonly FairUseTier[] = ["standard", "pro", "unlimited"]

/**
 * A stored allowlist, defensively.
 *
 * `null`, an absent key, a non-array and an empty list ALL mean "every plan":
 * the backend deletes the key rather than persisting `[]`, precisely so there
 * is one unrestricted state instead of two. Unknown ids are dropped here
 * (they cannot be rendered as a checkbox), and a list that contained nothing
 * else collapses back to "every plan" rather than to "nobody".
 */
export function normalizeModelPlans(plans: unknown): FairUseTier[] | null {
  if (!Array.isArray(plans)) return null
  const known = MODEL_PLAN_IDS.filter((plan) => plans.includes(plan))
  return known.length > 0 ? [...known] : null
}

/**
 * Toggle one plan in an allowlist.
 *
 * Unchecking the last remaining plan clears the restriction instead of
 * producing an empty list: a model available to no plan is not a state the
 * platform stores, and offering it in the UI would only ever end in a
 * restriction that silently became "everyone" on save.
 */
export function toggleModelPlan(
  plans: FairUseTier[] | null,
  plan: FairUseTier,
  checked: boolean
): FairUseTier[] | null {
  const current = new Set(plans ?? MODEL_PLAN_IDS)
  if (checked) current.add(plan)
  else current.delete(plan)
  const next = MODEL_PLAN_IDS.filter((candidate) => current.has(candidate))
  if (next.length === 0 || next.length === MODEL_PLAN_IDS.length) return null
  return next
}

/** Whether two allowlists say the same thing, so an unchanged dialog writes nothing. */
export function modelPlansEqual(left: FairUseTier[] | null, right: FairUseTier[] | null): boolean {
  const a = normalizeModelPlans(left)
  const b = normalizeModelPlans(right)
  if (a === null || b === null) return a === b
  return a.length === b.length && a.every((plan, index) => plan === b[index])
}
